import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, input, OnInit, signal, viewChild } from '@angular/core';
import { ICentralUserInfo } from '@app/shared/models';
import { AccountManagerService } from '@app/api/account/account-manager.service';
import { AccountSessionState } from '@app/common/services/account-session-state.service';
import { AccountSwitchService } from '@app/common/services/account-switch.service';
import { BroadcastService } from '@app/common/services/broadcast.service';
import { ErrorHandlerService } from '@app/core/services';
import { filter, take } from 'rxjs';
import { IcAccount } from '@app/api/account/models/ic-account.model';
import { MenuItem } from 'primeng/api';
import { Router } from '@angular/router';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { IconSvgComponent } from '@app/shared/icon-svg/icon-svg.component';
import { ToolbarAccountSelectComponent } from './toolbars/toolbar-account-select/toolbar-account-select.component';
import { ToolbarAdminAccountComponent } from './toolbars/toolbar-admin-account/toolbar-admin-account.component';
import { ToolbarWeatherComponent } from './toolbars/toolbar-weather/toolbar-weather.component';
import { ToolbarAlertsComponent } from './toolbars/toolbar-alerts/toolbar-alerts.component';
import { ToolbarUserComponent } from './toolbars/toolbar-user/toolbar-user.component';
import { Bind } from 'primeng/bind';
import { Menu } from 'primeng/menu';
import { NgOptimizedImage } from '@angular/common';


@Component({
    selector: 'ic-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    // OnPush (AB#24384). It hosts ic-toolbar-account-select, whose state is signals since AB#23266 slice 12, so the
    // picker redraws itself; that is the hosting rule in CODING-STANDARDS 5.2. AB#24308 had put it back to Eager while
    // the picker still used plain fields.
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgOptimizedImage,
        IconSvgComponent,
        ToolbarAccountSelectComponent,
        ToolbarAdminAccountComponent,
        ToolbarWeatherComponent,
        ToolbarAlertsComponent,
        ToolbarUserComponent,
        Bind,
        Menu
    ]
})
export class HeaderComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);

    private accountManager = inject(AccountManagerService);
    private accountSwitch = inject(AccountSwitchService);
    private broadcastService = inject(BroadcastService);
    private errorHandler = inject(ErrorHandlerService);
    private router = inject(Router);
    private session = inject(AccountSessionState);
    private translateService = inject(TranslateService);

    // Signal input: the shell sets it when the profile call returns, after the first render (AB#23266).
    readonly user = input<ICentralUserInfo | undefined>(undefined);
    /** Set by the shell when the profile call failed, so `user` will never arrive (ER-1543). */
    readonly userFailed = input(false);

    items: MenuItem[] = [];
    // A signal, so the weather and alerts toolbars follow the account without zone.js (ADR 0008).
    readonly account = signal<IcAccount | undefined>(undefined);
    private selectedAccountId = '';
    private readonly accountLookupFailed = signal(false);

    /**
     * Whether the header weather should hold its skeleton for a ZIP that has not arrived yet (ER-1543): while a chosen
     * account is being fetched, until the profile lands, and for a customer until boot has chosen an account. A failed
     * profile read, a customer whose boot settled with no account or failed, a Toro user with no stored default and a
     * failed lookup never get a ZIP, so each ends the skeleton and shows '--'.
     */
    protected readonly weatherLocationPending = computed(() => {
        if (this.account() !== undefined || this.accountLookupFailed()) { return false; }
        if (this.session.selectedAccountId() != null) { return true; }

        const user = this.user();
        if (user === undefined) { return !this.userFailed(); }
        // A Toro session never boots through AccountSwitchService; its stored default, if any, arrives as a broadcast.
        if (user.isToroUser()) { return false; }
        return this.session.bootState() === 'pending';
    });

    private readonly weatherAlerts = viewChild<ElementRef<HTMLElement>>('weatherAlerts');
    private readonly weatherGroup = viewChild<ElementRef<HTMLElement>>('weatherGroup');

    /**
     * Whether the weather on its own is wider than the room the header leaves it (ER-1572). What does not fit on the
     * container's line wraps out of sight in order, so a weather that cannot fit would take the alerts with it and leave
     * the space blank. While this is set the weather is laid out after the alerts, which show in its place if they fit.
     */
    protected readonly weatherTooWide = signal(false);

    constructor() {
        // A customer's boot selection belongs to AccountSwitchService, through the toolbars, and the header only
        // follows it. Toro users have no toolbar, so for them the header asks AccountSwitchService to announce the
        // stored preference, once per session. It follows the input because the user usually arrives after init.
        toObservable(this.user)
            .pipe(
                filter((user: ICentralUserInfo | undefined) => user?.isToroUser() === true),
                take(1),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => this.accountSwitch.announceStoredDefault());

        // The room is the container's content box; the weather never shrinks, so its border box is what it needs. Moving
        // the weather changes neither, so the answer cannot flip back and forth. Zoneless-safe: the observer writes a
        // signal, which schedules the re-render.
        effect(onCleanup => {
            const container = this.weatherAlerts()?.nativeElement;
            const weather = this.weatherGroup()?.nativeElement;
            if (!container || !weather || typeof ResizeObserver === 'undefined') {
                return;
            }
            // An entry comes only for what changed size, so each keeps its last reading.
            let room = 0;
            let needed = 0;
            const observer = new ResizeObserver(entries => {
                for (const entry of entries) {
                    if (entry.target === container) {
                        room = entry.contentBoxSize[0].inlineSize;
                    } else {
                        needed = entry.borderBoxSize[0].inlineSize;
                    }
                }
                this.weatherTooWide.set(needed > room);
            });
            observer.observe(container);
            observer.observe(weather, { box: 'border-box' });
            onCleanup(() => observer.disconnect());
        });
    }

    ngOnInit() {
        this.items = [
            {
                label: this.translateService.instant('MENUS.SIGN_OUT'),
                icon: 'fa fa-sign-out',
                command: this.onLogoutClick.bind(this)
            },
            {
                label: this.translateService.instant('MENUS.PROFILE'),
                icon: 'fa fa-user',
                command: this.onProfileClicked.bind(this)
            }
        ];

        this.broadcastService.selectedAccountChange
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((accountId: string) => this.selectedAccountId !== accountId),
            )
            .subscribe((accountId: string) => {
                this.selectedAccountId = accountId;
                this.accountLookupFailed.set(false);
                this.accountManager.getAccountById(accountId)
                    .subscribe({
                        next: account => this.account.set(account),
                        error: (err: unknown) => {
                            // A lookup a newer selection has overtaken must not end that one's skeleton.
                            if (accountId === this.selectedAccountId) { this.accountLookupFailed.set(true); }
                            this.errorHandler.handleError(err);
                        },
                    });
            });

        // Only the selected account feeds the weather zip and the alerts badge; an edit to any other
        // account (a sub-account, or one reached from a controller) must not repoint them. The cache
        // was refreshed before this fires, so a plain read is already current - no second GET.
        this.broadcastService.accountUpdated
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((accountId: string) => accountId === this.selectedAccountId),
            )
            .subscribe((accountId: string) => {
                this.accountManager.getAccountById(accountId)
                    .subscribe(account => this.account.set(account));
            });
    }

    // =========================================================================================================================================================
    // Event Handlers
    // =========================================================================================================================================================

    onLogoutClick() {
        this.router.navigate(['/logout']);
    }

    onProfileClicked() {
      this.router.navigate(['/user/user-profile']);
    }
    onToggleMenu() {
        this.broadcastService.toggleMobileMenu.next();
    }

    onLogoClick() {
        this.router.navigate(['/']);
    }
}
